import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { TooltipText } from '@/components/tooltip-text';
import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  SocialConnection,
  useConnectDiscord,
  useConnectTwitter,
  useDisconnectDiscord,
  useDisconnectTwitter,
  useSocialConnections,
} from '@/hooks/useSocialConnections';
import { Box, Card, Circle, Heading, HStack, Icon, IconButton, Image, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { FaDiscord } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { GoPencil } from 'react-icons/go';
import { IoClose } from 'react-icons/io5';
import { CardWithToolip } from './CardWithTooltip';
import { getTierDataById, SkySocietyTierData, skySocietyTiers } from './skySocietyTiers';

const SkySocietyTierAccordion = ({
  skySocietyTier,
  currentSkySocietyTier,
}: {
  skySocietyTier: SkySocietyTierData;
  currentSkySocietyTier: SkySocietyTierData;
}) => (
  <AccordionItem
    key={skySocietyTier.id}
    value={skySocietyTier.id}
    borderColor="border"
    borderWidth="thin"
    borderRadius="2xl"
    mb={4}
  >
    <AccordionItemTrigger h={112} px={4} cursor="pointer" _hover={{ bgColor: 'bg.subtle' }}>
      <HStack justifyContent="space-between" w="full">
        <HStack gap={4}>
          <Image boxSize={16} src={skySocietyTier.iconPath} />
          <VStack alignItems="start">
            <Text fontSize="lg" fontFamily="Lufga, sans-serif">
              {skySocietyTier.name}
            </Text>
            <Text color="fg.muted">Tier {skySocietyTier.tierNumber}</Text>
          </VStack>
        </HStack>
        {skySocietyTier.id === currentSkySocietyTier.id && (
          <RoundedBadge bgColor="rgba(0, 213, 148, 0.05)" p={2}>
            <Circle size={2} bgColor="rgba(35, 107, 83, 1)" mr={1} />
            Current Tier
          </RoundedBadge>
        )}
      </HStack>
    </AccordionItemTrigger>

    <AccordionItemContent p={4}>
      <VStack alignItems="stretch" gap={4}>
        <HStack alignItems="stretch">
          <CardWithToolip
            title="Badges Required"
            titleTooltip="The number of badges you need to earn to qualify for this tier."
            body={skySocietyTier.requirements.badges.description}
            cardBodyProps={{ p: 4 }}
          />
          <CardWithToolip
            title="Discord Role Required"
            titleTooltip="The Discord role you need to have in the official Discord server."
            body={skySocietyTier.requirements.discord.description}
            cardBodyProps={{ p: 4 }}
          />
        </HStack>

        {skySocietyTier.requirements.metrics.conditions[0] !== 'None' && (
          <>
            <TooltipText
              text="Portal Metrics Required"
              content="The activity metrics you need to achieve to qualify for this tier."
            />
            <HStack flexWrap="wrap" gap={2}>
              {skySocietyTier.requirements.metrics.conditions.map((condition, index) => (
                <HStack key={index}>
                  <RoundedBadge bgColor="muted" p={2}>
                    {condition}
                  </RoundedBadge>
                  {index < skySocietyTier.requirements.metrics.conditions.length - 1 && (
                    <Text>{skySocietyTier.requirements.metrics.logicOperator.toLowerCase()}</Text>
                  )}
                </HStack>
              ))}
            </HStack>
          </>
        )}

        <TooltipText text="Rewards" content="Benefits you receive for achieving this tier." />
        <HStack flexWrap="wrap" gap={2}>
          {skySocietyTier.rewards.plumePoints > 0 && (
            <RoundedBadge bgColor="muted" p={2}>
              <Image src="/assets/pp-circle.svg" boxSize={5} /> + {skySocietyTier.rewards.plumePoints} PP
            </RoundedBadge>
          )}
          {skySocietyTier.rewards.other?.map((reward, index) => (
            <RoundedBadge key={index} bgColor="muted" p={2}>
              {reward}
            </RoundedBadge>
          ))}
        </HStack>
      </VStack>
    </AccordionItemContent>
  </AccordionItem>
);

const SkySocietyModalContent = ({
  skySocietyTiers,
  currentSkySocietyTier,
}: {
  skySocietyTiers: SkySocietyTierData[];
  currentSkySocietyTier: SkySocietyTierData;
}) => {
  return (
    <DialogContent borderRadius={'3xl'} pb={4} h={825}>
      <DialogHeader mx="auto">
        <VStack gap={3} alignItems="center">
          <Heading size="3xl" fontFamily="Lufga, sans-serif">
            Sky Society
          </Heading>
          <Text fontSize="medium" color="fg.muted" fontFamily="Lufga, sans-serif" textAlign="center">
            Earn rewards for activity, participation, and loyalty.
            <br />
            Progress by collecting badges and unlocking tiers
          </Text>
        </VStack>
      </DialogHeader>
      <DialogBody overflow="scroll">
        <AccordionRoot collapsible>
          {skySocietyTiers.map((skySocietyTier) => (
            <SkySocietyTierAccordion
              key={skySocietyTier.id}
              skySocietyTier={skySocietyTier}
              currentSkySocietyTier={currentSkySocietyTier}
            />
          ))}
        </AccordionRoot>
      </DialogBody>
      <DialogCloseTrigger />
    </DialogContent>
  );
};

const ConnectedAccountsModal = ({
  connections,
  isOpen,
  onClose,
  connectDiscord,
  connectTwitter,
  disconnectDiscord,
  disconnectTwitter,
  isConnectingDiscord,
  isConnectingTwitter,
  isDisconnectingDiscord,
  isDisconnectingTwitter,
}: {
  connections: SocialConnection | undefined;
  isOpen: boolean;
  onClose: () => void;
  connectDiscord: () => void;
  connectTwitter: () => void;
  disconnectDiscord: () => void;
  disconnectTwitter: () => void;
  isConnectingDiscord: boolean;
  isConnectingTwitter: boolean;
  isDisconnectingDiscord: boolean;
  isDisconnectingTwitter: boolean;
}) => {
  const hasDiscordConnection = !!connections?.discord?.username;
  const hasTwitterConnection = !!connections?.twitter?.username;

  return (
    <DialogRoot open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent borderRadius="3xl" minW={{ base: 'none', md: 'xl' }}>
        <DialogHeader>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Heading
              size={{ base: '2xl', md: '4xl' }}
              textAlign="center"
              w="full"
              fontFamily="Lufga, sans-serif"
              my={4}
            >
              Connected Accounts
            </Heading>
            <IconButton onClick={onClose} variant="ghost" size="sm" rounded="full">
              <Icon as={IoClose} boxSize={6} />
            </IconButton>
          </HStack>
        </DialogHeader>

        <DialogBody p={{ base: 4, md: 8 }} pt={0}>
          <VStack gap={4} alignItems="stretch">
            {/* Twitter Connection */}
            <HStack
              justifyContent="space-between"
              alignItems={{ base: 'start', md: 'center' }}
              p={4}
              border="1px solid"
              borderColor="border"
              borderRadius="2xl"
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <HStack gap={4}>
                <Box p={3} bg="black" borderRadius="xl">
                  <Icon as={FaXTwitter} color="white" boxSize={6} />
                </Box>
                <VStack alignItems="start" gap={1}>
                  <Text fontSize="lg" fontWeight="600" fontFamily="Lufga, sans-serif">
                    Twitter (X)
                  </Text>
                  <Text color="fg.muted" fontSize="sm">
                    {hasTwitterConnection ? `@${connections?.twitter?.username}` : 'Not Connected'}
                  </Text>
                </VStack>
              </HStack>

              {hasTwitterConnection ? (
                <RoundedButton
                  bg="#FCEDE6"
                  color="#8D361C"
                  onClick={disconnectTwitter}
                  loading={isDisconnectingTwitter}
                >
                  Disconnect Twitter
                </RoundedButton>
              ) : (
                <RoundedButton variant="outline" onClick={connectTwitter} loading={isConnectingTwitter}>
                  Connect Twitter
                </RoundedButton>
              )}
            </HStack>

            {/* Discord Connection */}
            <HStack
              justifyContent="space-between"
              alignItems={{ base: 'start', md: 'center' }}
              p={4}
              border="1px solid"
              borderColor="border"
              borderRadius="2xl"
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <HStack gap={4}>
                <Box p={3} bg="#5865F2" borderRadius="xl">
                  <Icon as={FaDiscord} color="white" boxSize={6} />
                </Box>
                <VStack alignItems="start" gap={1}>
                  <Text fontSize="lg" fontWeight="600" fontFamily="Lufga, sans-serif">
                    Discord
                  </Text>
                  <Text color="fg.muted" fontSize="sm">
                    {hasDiscordConnection ? `@${connections?.discord?.username}` : 'Not Connected'}
                  </Text>
                </VStack>
              </HStack>

              {hasDiscordConnection ? (
                <RoundedButton
                  bg="#FCEDE6"
                  color="#8D361C"
                  onClick={disconnectDiscord}
                  loading={isDisconnectingDiscord}
                >
                  Disconnect Discord
                </RoundedButton>
              ) : (
                <RoundedButton variant="outline" onClick={connectDiscord} loading={isConnectingDiscord}>
                  Connect Discord
                </RoundedButton>
              )}
            </HStack>

            {/* Note */}
            <Box fontFamily="Lufga, sans-serif">
              <Text fontSize="lg" color="fg.muted" fontWeight="500">
                <Text as="span" color="fg">
                  Note:
                </Text>{' '}
                Disconnecting an account will result in the loss of all your Sky Society progress. You can restore your
                progress at any time by reconnecting the same account.
              </Text>
            </Box>
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export const SkySocietyCard = ({
  isCurrentUserAddress,
  profileAddress,
}: {
  isCurrentUserAddress: boolean;
  profileAddress: string;
}) => {
  // Add social connection hooks
  const { data: connections } = useSocialConnections(profileAddress);
  const { mutate: connectDiscord, isPending: isConnectingDiscord } = useConnectDiscord();
  const { mutate: connectTwitter, isPending: isConnectingTwitter } = useConnectTwitter();
  const { mutate: disconnectDiscord, isPending: isDisconnectingDiscord } = useDisconnectDiscord();
  const { mutate: disconnectTwitter, isPending: isDisconnectingTwitter } = useDisconnectTwitter();

  // State for Connected Accounts modal
  const [isConnectedAccountsModalOpen, setIsConnectedAccountsModalOpen] = useState(false);

  // Get the current tier data using the helper
  const currentSkySocietyTier = getTierDataById(connections?.skySocietyTier || 'egg');

  // Check if social accounts are connected
  const hasDiscordConnection = !!connections?.discord?.username;
  const hasTwitterConnection = !!connections?.twitter?.username;

  return (
    <>
      <DialogRoot size="md">
        <DialogTrigger asChild>
          <Card.Root
            flex={1}
            borderRadius={'2xl'}
            cursor="pointer"
            className="group"
            _hover={{ transform: 'scale(0.985)', transition: 'transform 0.3s' }}
          >
            <Card.Header>
              <HStack justifyContent="space-between">
                <Card.Title fontFamily="Lufga, sans-serif" fontWeight={'700'} fontSize={'2xl'}>
                  Sky Society
                </Card.Title>
              </HStack>
            </Card.Header>
            <Card.Body gap={4} pt={4}>
              <HStack justifyContent="space-between" alignItems="center">
                <HStack gap={4}>
                  <Image src={currentSkySocietyTier.iconPath} boxSize={16} />
                  <VStack flex={1} alignItems="start">
                    <Text fontSize="sm" color="fg.muted" fontWeight="500">
                      Current Tier
                    </Text>
                    <Box fontFamily="Lufga, sans-serif" fontWeight={'500'} fontSize={'lg'}>
                      {currentSkySocietyTier.name}
                    </Box>
                  </VStack>
                </HStack>
              </HStack>
              <Text fontFamily="Lufga, sans-serif" color="fg.muted" fontWeight={500} fontSize="lg">
                Level up in the Sky Society by being active in Discord, on Twitter, and throughout Plume's RWAfi
                ecosystem
              </Text>
              {isCurrentUserAddress && (
                <HStack flexWrap={['wrap']} gap={4} w="full">
                  {/* Twitter button */}
                  {hasTwitterConnection ? (
                    <RoundedButton variant="outline" fontSize={'sm'} px={4} disabled bgColor="bg.muted">
                      <Icon as={FaXTwitter} mr={1} />@{connections?.twitter?.username}
                    </RoundedButton>
                  ) : (
                    <RoundedButton
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        connectTwitter();
                      }}
                      loading={isConnectingTwitter}
                      fontSize={'sm'}
                      px={4}
                    >
                      Connect X
                    </RoundedButton>
                  )}

                  {/* Discord button */}
                  {hasDiscordConnection ? (
                    <RoundedButton variant="outline" fontSize={'sm'} px={4} disabled bgColor="bg.muted">
                      <Icon as={FaDiscord} mr={1} />@{connections?.discord?.username}
                    </RoundedButton>
                  ) : (
                    <RoundedButton
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        connectDiscord();
                      }}
                      loading={isConnectingDiscord}
                      fontSize={'sm'}
                      px={4}
                    >
                      Connect Discord
                    </RoundedButton>
                  )}

                  {(hasDiscordConnection || hasTwitterConnection) && (
                    <IconButton
                      rounded="full"
                      variant="outline"
                      fontSize={'sm'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsConnectedAccountsModalOpen(true);
                      }}
                    >
                      <Icon as={GoPencil} boxSize={4} />
                    </IconButton>
                  )}
                </HStack>
              )}
            </Card.Body>
          </Card.Root>
        </DialogTrigger>
        <SkySocietyModalContent skySocietyTiers={skySocietyTiers} currentSkySocietyTier={currentSkySocietyTier} />
      </DialogRoot>

      {/* Connected Accounts Modal */}
      <ConnectedAccountsModal
        connections={connections}
        isOpen={isConnectedAccountsModalOpen}
        onClose={() => setIsConnectedAccountsModalOpen(false)}
        connectDiscord={connectDiscord}
        connectTwitter={connectTwitter}
        disconnectDiscord={disconnectDiscord}
        disconnectTwitter={disconnectTwitter}
        isConnectingDiscord={isConnectingDiscord}
        isConnectingTwitter={isConnectingTwitter}
        isDisconnectingDiscord={isDisconnectingDiscord}
        isDisconnectingTwitter={isDisconnectingTwitter}
      />
    </>
  );
};
